import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import { Hotel } from '../../../types/hotel';
import { APIHOST } from '../../../common';
import { getToken } from '../../../components/auth/AuthContext';

interface HotelFormData {
  name: string;
  longName: string;
  addressStreet1: string;
  addressStreet2: string;
  addressCity: string;
  addressState: string;
  addressZip: string;
  website: string;
  amenities: string;
}

interface AddEditHotelDialogProps {
  open: boolean;
  onClose: () => void;
  editingHotel: Hotel | null;
  onSuccess: () => void;
}

export default function AddEditHotelDialog({ 
  open, 
  onClose, 
  editingHotel, 
  onSuccess 
}: AddEditHotelDialogProps) {
  const [formData, setFormData] = useState<HotelFormData>({
    name: editingHotel?.name || '',
    longName: editingHotel?.longName || '',
    addressStreet1: editingHotel?.addressStreet1 || '',
    addressStreet2: editingHotel?.addressStreet2 || '',
    addressCity: editingHotel?.addressCity || '',
    addressState: editingHotel?.addressState || '',
    addressZip: editingHotel?.addressZip || '',
    website: editingHotel?.website || '',
    amenities: editingHotel?.amenities || ''
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  // Update form data when editingHotel changes
  useEffect(() => {
    if (editingHotel) {
      setFormData({
        name: editingHotel.name || '',
        longName: editingHotel.longName || '',
        addressStreet1: editingHotel.addressStreet1 || '',
        addressStreet2: editingHotel.addressStreet2 || '',
        addressCity: editingHotel.addressCity || '',
        addressState: editingHotel.addressState || '',
        addressZip: editingHotel.addressZip || '',
        website: editingHotel.website || '',
        amenities: editingHotel.amenities || ''
      });
    } else {
      setFormData({
        name: '',
        longName: '',
        addressStreet1: '',
        addressStreet2: '',
        addressCity: '',
        addressState: '',
        addressZip: '',
        website: '',
        amenities: ''
      });
    }
    setFormError('');
  }, [editingHotel, open]);

  const handleFormChange = (field: keyof HotelFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFormSubmit = async () => {
    try {
      setFormLoading(true);
      setFormError('');

      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      if (!formData.name || !formData.longName) {
        throw new Error('Hotel name and full name are required');
      }

      const method = editingHotel ? 'PUT' : 'POST';
      const url = editingHotel 
        ? `${APIHOST}/api/hotels/${editingHotel.documentId}`
        : `${APIHOST}/api/hotels`;

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            name: formData.name,
            longName: formData.longName,
            addressStreet1: formData.addressStreet1 || null,
            addressStreet2: formData.addressStreet2 || null,
            addressCity: formData.addressCity || null,
            addressState: formData.addressState || null,
            addressZip: formData.addressZip || null,
            website: formData.website || null,
            amenities: formData.amenities || null
          }
        })
      });

      if (!response.ok) {
        throw new Error(editingHotel ? 'Failed to update hotel' : 'Failed to create hotel');
      }

      onSuccess();
      onClose();

    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save hotel');
    } finally {
      setFormLoading(false);
    }
  };

  const handleClose = () => {
    setFormError('');
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {editingHotel ? 'Edit Hotel' : 'Add New Hotel'}
      </DialogTitle>
      <DialogContent>
        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}
        
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Hotel Name (Short)"
              value={formData.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              fullWidth
              required
              placeholder="e.g., Marriott Downtown"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Hotel Name (Full)"
              value={formData.longName}
              onChange={(e) => handleFormChange('longName', e.target.value)}
              fullWidth
              required
              placeholder="e.g., Downtown Marriott Hotel & Convention Center"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Street Address 1"
              value={formData.addressStreet1}
              onChange={(e) => handleFormChange('addressStreet1', e.target.value)}
              fullWidth
              placeholder="e.g., 123 Main St"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Street Address 2"
              value={formData.addressStreet2}
              onChange={(e) => handleFormChange('addressStreet2', e.target.value)}
              fullWidth
              placeholder="e.g., Suite 400 (optional)"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="City"
              value={formData.addressCity}
              onChange={(e) => handleFormChange('addressCity', e.target.value)}
              fullWidth
              placeholder="e.g., San Francisco"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="State"
              value={formData.addressState}
              onChange={(e) => handleFormChange('addressState', e.target.value)}
              fullWidth
              placeholder="e.g., CA"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="ZIP Code"
              value={formData.addressZip}
              onChange={(e) => handleFormChange('addressZip', e.target.value)}
              fullWidth
              placeholder="e.g., 94102"
            />
          </Grid>
          
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Website"
              value={formData.website}
              onChange={(e) => handleFormChange('website', e.target.value)}
              fullWidth
              placeholder="https://www.hotel-website.com"
            />
          </Grid>
          
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Amenities"
              value={formData.amenities}
              onChange={(e) => handleFormChange('amenities', e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="e.g., Pool, Fitness Center, Business Center, Free WiFi"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={formLoading}>
          Cancel
        </Button>
        <Button 
          onClick={handleFormSubmit} 
          variant="contained" 
          disabled={formLoading || !formData.name || !formData.longName}
        >
          {formLoading ? <CircularProgress size={20} /> : (editingHotel ? 'Update Hotel' : 'Add Hotel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}